import React from 'react';
import type { PeriodStats, ReportDetailLabels } from '../../../shared/types/reports';
import { DoughnutChart } from '../charts/DoughnutChart';
import { MOOD_RANGE_COLORS } from '../../../shared/utils';

export interface MoodDistributionChartProps {
  currentPeriodStats: PeriodStats;
  labels: ReportDetailLabels;
}

const MOOD_RANGE_ORDER = ['very_low', 'low', 'neutral', 'good', 'great'];

export function MoodDistributionChart({
  currentPeriodStats,
  labels,
}: MoodDistributionChartProps) {
  const distribution = currentPeriodStats.moodDistribution;

  // Build data in consistent order
  const chartData: number[] = [];
  const chartLabels: string[] = [];
  const chartColors: string[] = [];

  for (const range of MOOD_RANGE_ORDER) {
    const count = distribution[range] || 0;
    if (count > 0) {
      chartData.push(count);
      chartLabels.push(getMoodRangeLabel(range, labels));
      chartColors.push(MOOD_RANGE_COLORS[range] || '#6c757d');
    }
  }

  if (chartData.length === 0) {
    return (
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">{labels.moodDistribution}</h5>
        </div>
        <div className="card-body d-flex align-items-center justify-content-center">
          <p className="text-muted mb-0">No mood data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-header bg-white border-0">
        <h5 className="mb-0">
          <i className="bi bi-pie-chart me-2"></i>
          {labels.moodDistribution}
        </h5>
      </div>
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-6">
            <DoughnutChart
              data={chartData}
              labels={chartLabels}
              colors={chartColors}
              height={220}
              showLegend={false}
              cutout="60%"
            />
          </div>
          <div className="col-md-6">
            <div className="d-flex flex-column gap-2">
              {MOOD_RANGE_ORDER.map(range => {
                const count = distribution[range] || 0;
                if (count === 0) return null;
                const total = Object.values(distribution).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={range} className="d-flex align-items-center gap-2">
                    <span
                      className="rounded-circle"
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: MOOD_RANGE_COLORS[range],
                      }}
                    ></span>
                    <span className="flex-grow-1">{getMoodRangeLabel(range, labels)}</span>
                    <span className="badge bg-light text-dark">
                      {count} ({percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getMoodRangeLabel(range: string, labels: ReportDetailLabels): string {
  const labelMap: Record<string, string> = {
    very_low: labels.veryLow,
    low: labels.low,
    neutral: labels.neutral,
    good: labels.good,
    great: labels.great,
  };
  return labelMap[range] || range;
}

export default MoodDistributionChart;
